// "Any 3 small tins" deal page (snippets/page-any-3-teas.liquid).
// The basket is the only record (Michael 2026-10-02): a card's Add puts that
// tea's small tin straight in it, and once it's in, the card shows the
// product page's quantity stepper, pointed at that basket line - so - and +
// go through cardo like the cart drawer's. Cards and the bar always show
// what is in the basket, however it got there. The deal price itself will
// come from our discount app, in the cart.
//
//   [data-deal-add="<variant id>"][data-deal-handle]  a card's Add button
//   [data-deal-stepper="<variant id>"]  its stepper (snippets/quantity-input)
//   [data-deal-bar]  the bar: data-deal-tin-ids (which tins count, | joined),
//                    data-deal-size (3), prices in cents: data-deal-set-price
//                    (all 3), data-deal-extra-price (each tin after), data-deal-was (usual)
//
// ?tea=<handle> puts nothing in the basket (Michael 2026-10-13): that tea
// takes the first Top sellers spot (page-any-3-teas.liquid).
//
// Trio ads (2026-10-09, trios in snippets/deal-trios, on the bar as
// data-deal-trios="key::Name::h,h,h[::bar line]|..."): ?trio=<key>, e.g. ?trio=earl-grey,
// puts that trio's three small tins in the basket on arrival and opens the
// basket (Michael 2026-10-10: no trio cards on the page). Once per visit,
// and only once the page is on screen: never while it is only being
// prerendered or loaded in the background (an app opening the ad's page
// behind the feed, 2026-10-12). Tins already in the basket are skipped;
// sold-out tins are skipped and named in the bar: a tea the page drew as sold
// out has a hidden card and is on the bar's data-deal-sold-out
// ("handle::Title|..."); the shop's live stock (live-stock.js, Michael
// 2026-10-26) can bring its card back, or mark a card's Add sold out, and
// the link waits for that answer.
// While the basket holds exactly the trio, the bar names it; a swap is
// still any 3.
//
// Trio pages (/pages/any-3-teas-<key>, page-trio.liquid, Michael 2026-10-12):
// nothing on arrival. The trio's tea cards ([data-deal-trio-tea="<small tin
// id>"][data-deal-title]) say which are sold out (data-sold-out, following the
// shop's live stock); [data-deal-trio-add] buttons (on the page and in the
// bar) add the in-stock ones not in the basket yet and open the basket
// (Michael 2026-10-12: "Basket should open"). Their words go by how many are
// in stock (data-deal-trio-words with all three), and "Pick a third tea"
// ([data-deal-trio-pick]) shows while one is sold out. The buttons hide
// once the basket holds all of them; the bar then names the trio as above.
//
// The deal is for Meta ad taps (?hd=<code>, deals 0.9.0): without a live
// pass the page shows an "offer ended" state instead (deal-unlock.js).
// ?trio= adds only once the ad code is answered, and only for a
// live pass (or one that never came: Checkout then offers the deal-fail
// page's refund) - a shared link without a pass adds nothing - and only
// once the pass is on the cart, so the basket shows the deal price (review
// 2026-10-10).
import { addToCart } from '@split-infinity/cardo/cart'
import { openDrawer } from '@split-infinity/cardo/drawer'
import { unlockState, settled, passOnCart, passReady } from './deal-unlock.js'
import { shippingCents } from './shipping-choice.js'
import { stockReady } from './live-stock.js'

const bar = () => document.querySelector('[data-deal-bar]')
const ordinal = (n) => ({ 1: '1st', 2: '2nd', 3: '3rd' })[n] || `${n}th`
const money = (cents) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: cents % 100 ? 2 : 0 }).format(cents / 100)

let cart = null
// Keeps a phrase on one line: a long line breaks only between phrases
// (Michael 2026-10-09), e.g. "Duchess of Bedford / is in your basket".
const whole = (phrase) => phrase.replaceAll(' ', '\u00a0')
// The trio from a ?trio= link: { name, line, ids, soldOut: [titles] }.
let trio = null

const soldOutTitle = (handle) =>
  (bar().dataset.dealSoldOut || '')
    .split('|')
    .map((entry) => entry.split('::'))
    .find(([h]) => h === handle)?.[1]

// A trio's tins by its key: the in-stock ones' small-tin ids (from their tea
// cards) and the sold-out ones' titles. A handle that is neither is skipped.
function trioOf(key) {
  const entry = (bar().dataset.dealTrios || '')
    .split('|')
    .map((e) => e.split('::'))
    .find(([k]) => k === key)
  if (!entry) return null
  const ids = []
  const soldOut = []
  for (const handle of entry[2].split(',')) {
    const button = document.querySelector(`[data-deal-add][data-deal-handle="${CSS.escape(handle)}"]`)
    const title = soldOutTitle(handle) || button?.closest('[data-selection-card]')?.querySelector('h3')?.textContent.trim()
    if (button && !button.hasAttribute('data-sold-out')) ids.push(button.dataset.dealAdd)
    else if (title) soldOut.push(title)
  }
  return { name: entry[1], line: entry[3], ids, soldOut }
}
// "A", "A and B", "A, B and C", each name kept whole.
const names = (list) => list.map(whole).join(', ').replace(/, ([^,]*)$/, ' and $1')

function render() {
  const el = bar()
  const tinIds = new Set((el.dataset.dealTinIds || '').trim().split('|'))
  const size = Number(el.dataset.dealSize)
  if (trio?.page) Object.assign(trio, trioTeas())
  const inBasket = new Map()
  let tins = 0
  for (const line of cart.items) {
    const id = String(line.variant_id)
    inBasket.set(id, (inBasket.get(id) || 0) + line.quantity)
    if (tinIds.has(id)) tins += line.quantity
  }

  // Wording agreed with Michael 2026-10-04 - each line says exactly what
  // the next tap gets them. Tin prices only; Checkout shows the whole basket.
  const set = Number(el.dataset.dealSetPrice)
  const extra = Number(el.dataset.dealExtraPrice)
  const was = Number(el.dataset.dealWas)
  const tinsPrice = tins < size ? tins * was : set + (tins - size) * extra
  const saving = tins * was - tinsPrice
  let status
  let offer
  // Trio wording approved by Michael 2026-10-10: a trio's name, then
  // the usual savings line.
  const others = trio && trio.soldOut.length
  if (others && tins < size) {
    status = `Sorry, ${names(trio.soldOut)} ${whole(others === 1 ? 'has sold out' : 'have sold out')}`
    offer = `${whole(others === 1 ? 'Pick another tin' : `Pick ${others} others`)} – ${whole(`all ${size} for ${money(set)}`)}`
  } else if (trio && tins === size && trio.ids.length === size && trio.ids.every((id) => inBasket.has(id))) {
    status = trio.line ? trio.line : `${whole(`Your ${trio.name}`)} ${whole('is in your basket')}`
    offer = `You're saving ${money(saving)} · add another for ${money(extra)}`
  } else if (tins === 0) {
    status = `Any ${size} small tins for ${money(set)}`
    offer = `Usually ${money(size * was)} · delivered free`
  } else if (tins < size - 1) {
    status = `${tins} of ${size} chosen`
    offer = `Add ${size - tins} more – all ${size} for ${money(set)}`
  } else if (tins === size - 1) {
    status = `${tins} of ${size} chosen`
    offer = `Add a ${ordinal(size)} tin for just ${money(set - tins * was)} more`
  } else if (tins === size) {
    status = `Deal unlocked – ${size} tins for ${money(set)}`
    offer = `You're saving ${money(saving)} · add another for ${money(extra)}`
  } else {
    status = `${tins} small tins for ${money(tinsPrice)}`
    offer = `You're saving ${money(saving)} · add another for ${money(extra)}`
  }
  // A trio page's add buttons go once its tins are all in.
  if (trio?.page) {
    const all = trio.ids.every((id) => inBasket.has(id))
    const n = trio.ids.length
    document.querySelectorAll('[data-deal-trio-add]').forEach((b) => {
      b.hidden = all
      b.textContent = n === size ? b.dataset.dealTrioWords : n === 2 ? 'Add the other two' : 'Add the other one'
    })
    document.querySelectorAll('[data-deal-trio-pick]').forEach((a) => (a.hidden = trio.ids.length >= size))
  }
  el.querySelector('[data-deal-status]').textContent = status
  el.querySelector('[data-deal-bar-offer]').textContent = offer

  // One dot per tin towards the deal; past it, the dots stop and the count speaks.
  el.querySelector('[data-deal-slots]').replaceChildren(
    ...Array.from({ length: size }, (_, i) => {
      const dot = document.createElement('span')
      dot.className = `block h-xs w-xs rounded-full border-2 border-gold ${i < tins ? 'bg-gold' : ''}`
      return dot
    }),
  )
  // Checkout shows the whole basket's total - it checks out everything in it.
  // cart.total_price arrives with never-shipped items (gift cards) taken off
  // for the free-shipping goal (shipping-choice.js); add them back here
  // (2026-10-07 re-review R4). Shipping too, as the basket's Checkout button
  // shows it (2026-10-11 review): the rate goes by what is shipped.
  const checkout = el.querySelector('[data-deal-checkout]')
  checkout.hidden = tins < size
  checkout.textContent = `Checkout · ${money(cart.total_price + (cart.unshipped_off ?? 0) + shippingCents(cart))}`

  // A tea has one card, but more than one basket line can hold it.
  const keys = new Map()
  for (const line of cart.items) {
    const id = String(line.variant_id)
    keys.set(id, keys.has(id) ? `${keys.get(id)},${line.key}` : line.key)
  }
  document.querySelectorAll('[data-deal-stepper]').forEach((stepper) => {
    const id = stepper.dataset.dealStepper
    const n = inBasket.get(id) || 0
    const add = stepper.parentElement.querySelector('[data-deal-add]')
    // Same grid cell, so swapping them never changes the card's size.
    add.classList.toggle('invisible', n > 0)
    add.inert = n > 0
    stepper.classList.toggle('invisible', n === 0)
    stepper.inert = n === 0
    const input = stepper.querySelector('input')
    if (n > 0) input.setAttribute('data-cardo-cart-qty', keys.get(id))
    else input.removeAttribute('data-cardo-cart-qty')
    // The value attribute too: live-stock.js starts a basket box from it.
    input.value = input.defaultValue = String(n)
    stepper.querySelector('[data-qty]')?.dispatchEvent(new CustomEvent('qty:sync', { bubbles: true }))
  })
}

// True once added. The button, if any, is disabled meanwhile.
async function add(ids, button) {
  if (button) button.disabled = true
  try {
    // A quick tap on arrival waits for the pass to be on the cart (deal-unlock.js).
    await passReady()
    await addToCart([].concat(ids).map((id) => ({ id: Number(id), quantity: 1 })), { context: 'deal' })
    return true
  } catch (error) {
    // Shopify's reason, e.g. that tea just sold out
    bar().querySelector('[data-deal-status]').textContent = error.message
    return false
  } finally {
    // Live stock may have marked it sold out meanwhile (live-stock.js).
    if (button) button.disabled = button.hasAttribute('data-sold-out')
  }
}

// Trio ad arrival: once per visit. Adds the tins not in the
// basket yet in one go, then opens the basket.
async function addTrioFromLink() {
  const key = new URLSearchParams(location.search).get('trio')?.trim().toLowerCase()
  trio = key && trioOf(key)
  if (!trio) return
  render()
  try {
    if (sessionStorage.getItem(`htwh:deal-trio:${key}`)) return
    sessionStorage.setItem(`htwh:deal-trio:${key}`, '1')
  } catch {
    /* private mode: still add once for this page view */
  }
  const missing = trio.ids.filter((id) => !cart.items.some((l) => String(l.variant_id) === id))
  if (missing.length && !(await add(missing))) return
  // With a tin sold out, the page's bar says so; the basket would hide it.
  if (trio.ids.length && !trio.soldOut.length) openDrawer('cart')
}

async function arrive() {
  if (!new URLSearchParams(location.search).get('trio')) return
  const state = await settled()
  if (state !== 'live' && state !== 'failed') return
  if (state === 'live') await passOnCart()
  // The shop's live stock, so a tea the page drew as sold out still goes in; no more than 3s.
  await Promise.race([stockReady, new Promise((resolve) => setTimeout(resolve, 3000))])
  addTrioFromLink()
}

// A trio page's teas, from their cards: in-stock small-tin ids, sold-out titles.
function trioTeas() {
  const ids = []
  const soldOut = []
  document.querySelectorAll('[data-deal-trio-tea]').forEach((card) => {
    if (card.dataset.dealTrioTea && !card.hasAttribute('data-sold-out')) ids.push(card.dataset.dealTrioTea)
    else soldOut.push(card.dataset.dealTitle)
  })
  return { ids, soldOut }
}

// A trio page's trio, from its add button and the bar's one trio.
function trioOfPage() {
  const button = document.querySelector('[data-deal-trio-add]')
  const entry = (bar().dataset.dealTrios || '').split('::')
  if (!button || !entry[1]) return null
  return { page: true, name: entry[1], line: entry[3], ...trioTeas() }
}

// Runs fn once the page is on screen: after a prerender once it is opened,
// for a page loaded in the background once it is shown.
function whenShown(fn) {
  const visible = () => {
    if (document.visibilityState !== 'hidden') return fn()
    const shown = () => {
      if (document.visibilityState !== 'visible') return
      document.removeEventListener('visibilitychange', shown)
      fn()
    }
    document.addEventListener('visibilitychange', shown)
  }
  if (document.prerendering) document.addEventListener('prerenderingchange', visible, { once: true })
  else visible()
}

function start() {
  if (!bar()) return
  trio = trioOfPage()
  // The ad pass (deal-unlock.js): live or being checked shows the teas; an
  // ended pass, or none, shows that state instead (page-any-3-teas.liquid).
  const page = bar().closest('[data-deal-page]')
  document.addEventListener('deal:unlock', () => {
    if (page) page.dataset.dealState = unlockState()
  })
  // The shop's live stock can change which of a trio page's teas are in stock.
  document.addEventListener('stock:live', () => {
    if (cart && trio?.page) render()
    // A group drawn with every tea sold out shows its tile again once one is back.
    document.querySelectorAll('[data-deal-type-tile][data-sold-out]').forEach((tile) => {
      if (document.querySelector(`[data-deal-type="${tile.dataset.dealTypeTile}"]:not([data-sold-out])`))
        tile.removeAttribute('data-sold-out')
    })
  })
  let first = true
  document.addEventListener('cardo:cart', (e) => {
    cart = e.detail.cart
    render()
    if (!first) return
    first = false
    whenShown(arrive)
  })
  let trioAdding = false
  document.addEventListener('click', (e) => {
    const button = e.target.closest('[data-deal-add]')
    if (button) add(button.dataset.dealAdd, button)
    const trioButton = e.target.closest('[data-deal-trio-add]')
    // One add at a time: the page's button and the bar's would otherwise both add the trio (review 2026-10-12).
    if (trioButton && cart && !trioAdding) {
      const missing = trio.ids.filter((id) => !cart.items.some((l) => String(l.variant_id) === id))
      if (!missing.length) openDrawer('cart')
      else {
        trioAdding = true
        add(missing, trioButton)
          .then((ok) => ok && openDrawer('cart'))
          .finally(() => (trioAdding = false))
      }
    }
  })
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true })
} else {
  start()
}
